// @ts-strict-ignore
import { Typography } from '@mui/material';
import { useSelectedAccountForOrderRequest } from 'components/AccountDropdown/Store/AccountSelectionStore';
import { TradeTicketAccountDropdown } from 'components/AccountDropdown/TradeTicketAccountDropdown';
import { QuantityInputSplit } from 'components/InputSplit/QuantityInputSplit';
import useOptionsOpenClose from 'hooks/useOptionsOpenClose';
import { T } from 'phoenix/assets/lang/T';
import { TradeActions } from 'phoenix/constants/Trade';
import { useMarketTimeSegmentV2 } from 'phoenix/hooks/useMarketTimeSegment';
import { useSnexStore } from 'phoenix/hooks/UseSnexStore';
import { useText } from 'phoenix/hooks/UseText';
import { EquitiesAssetClass } from 'phoenix/models/AssetClasses/EquitiesAssetClass';
import { FuturesAssetClass } from 'phoenix/models/AssetClasses/FuturesAssetClass';
import { useAssetClassMetaV2 } from 'phoenix/models/AssetClasses/useAssetClass';
import { GetFuturesContractsAction } from 'phoenix/redux/actions';
import { OptionSymbol } from 'phoenix/redux/models';
import { FuturesSymbol } from 'phoenix/redux/models/Futures/FuturesSymbol';
import { useSecurityMetadataV2 } from 'phoenix/stores/SecurityMetadataV2Store';
import { floatMath, FormatNumber, SafeFormat, toMoneyOpt2 } from 'phoenix/util';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Flex } from '../..';
import { TradeCancelHeader } from '../Headers/TradeCancelHeader';
import { TradeOptionHeader } from '../Headers/TradeOptionHeader';
import { GetTradeTicketQuote } from '../Shared/helpers';
import { ImmediateExecutionAdvisory } from '../Shared/ImmediateExecutionAdvisory';
import { TimeInForceSelect } from '../Shared/TimeInForceSelect';
import {
    AlgoControl,
    BidAskButtons, BuyingPowerDisplayV2,
    OptionContractSummaryRow,
    OptionTotalCostDisplay,
    OptionUnderlyingHoldingsDisplay,
    PriceInputSplit
} from '../Shared/TradeFormComponents';
import { TradeInputValidationButton } from '../Shared/TradeInputValidationButton';
import { TradeTicketSection } from '../Shared/TradeTicketSection';
import { OptionsTradeTicketViewModel } from '../Store/TradeTicketViewModel';
import { useTradeTicketViewModel } from '../Store/useTradeTicketViewModel';
import { useIsDelayedPricing } from 'phoenix/hooks/UseIsDelayedPricing';

type TradeTicketVariant = 'option-individual' | 'normal';

interface OptionIndivTradeInputPageProps {
    variant?: TradeTicketVariant;
    color?: string;
}

const priceInputFields = ['limitPrice', 'stopPrice'];

export const OptionIndivTradeInputPage = React.memo((props: OptionIndivTradeInputPageProps) => {
    const dispatch = useDispatch();
    const viewModel = useTradeTicketViewModel<OptionsTradeTicketViewModel>();
    const {
        awaitingConfirmation,
        displayOrderType,
        initialLimitPrice,
        initialQuantity,
        initialStopPrice,
        limitPrice,
        modifyingOrder,
        orderType,
        setViewModel,
        stopPrice,
        symbol = '',
        tradeAction = 'Buy',
        variant
    } = viewModel;
    // Use quantity input key to force a remount of the quantity input component
    // such as when using "Sell All" button to override user input
    const [quantityInputKey, setQuantityInputKey] = useState<number>(0);
    // Same as above but when Bid/Ask buttons are clicked to "reset" price
    const [limitInputKey, setLimitInputKey] = useState<number>(0);
    const [stopInputKey, setStopInputKey] = useState<number>(0);

    useEffect(() => {
        if (!variant) {
            setViewModel({ variant: props.variant });
        }
    }, [variant, props.variant, setViewModel]);

    const logo = useSnexStore((s) => s.logos.bySymbol[symbol]);
    const quote = GetTradeTicketQuote(symbol, useIsDelayedPricing(symbol));
    const text = useText((t) => t);
    const ac = useAssetClassMetaV2(symbol);
    const selectedAccountNumber = useSelectedAccountForOrderRequest(ac);
    const optSym = new OptionSymbol(symbol);
    const { loadData, getMetadataBySymbol } = useSecurityMetadataV2();
    const meta = getMetadataBySymbol(symbol);
    const { formatPrice, getPriceFormatInfo } = ac;
    const formatOptions = getPriceFormatInfo(meta);
    const { decimalPlaces, moneyFormatOptions, tickSize } = formatOptions;
    // Futures only - for enforcing tick size
    const [tickSizeError, setTickSizeError] = useState<string[]>([]);
    const isFuture = FuturesSymbol.IsFuturesSymbol(symbol);
    const fsym = useMemo(() => new FuturesSymbol(optSym.underlyingSymbol), [optSym.underlyingSymbol]);
    const base = useMemo(() => fsym.baseContract, [fsym.baseContract]);
    const userInfo = useSnexStore((s) => s.user?.myInfo.data);
    const { openClose } = useOptionsOpenClose();
    const action = text.tradeTicket.input.options.actionToOpenClose({ action: tradeAction }, openClose);
    const putCall = optSym.putCall ? T((t) => t.general.options.putCallWord(optSym.putCall)) : '';
    const showDollarSign = isFuture ? false : undefined;
    const [marketTimeSegment] = useMarketTimeSegmentV2();

    const { assetFamily, getErrorProps, hasAdvancedRouting, headerTitle, sharesLabel, useOptionsBuyingPower } = isFuture
        ? {
              assetFamily: FuturesAssetClass.family,
              getErrorProps: (field: 'stopPrice' | 'limitPrice'): { error?: boolean; helperText?: string } =>
                  tickSizeError.includes(field)
                      ? {
                            error: true,
                            helperText: meta?.formatting?.isFractional
                                ? text.tradeTicket.input.futures.enforceFractional
                                : text.tradeTicket.input.futures.enforceTickSize(tickSize)
                        }
                      : {},
              hasAdvancedRouting: false,
              headerTitle: fsym.noPrefix,
              sharesLabel: null,
              useOptionsBuyingPower: false
          }
        : {
              assetFamily: EquitiesAssetClass.family,
              getErrorProps: (): { error?: boolean; helperText?: string } => undefined,
              hasAdvancedRouting: userInfo?.hasAdvancedRouting,
              headerTitle: optSym.underlyingSymbol,
              sharesLabel: typeof meta?.sizing?.deliverableCount === 'number' && text.tradeTicket.input.options.sharesPerContract(meta?.sizing?.deliverableCount),
              useOptionsBuyingPower: true
          };

    const showAlgo = hasAdvancedRouting && marketTimeSegment === 'open';

    useEffect(() => {
        if (isFuture) {
            dispatch(GetFuturesContractsAction(symbol));
            loadData(symbol);
        }
    }, [base, dispatch, isFuture, loadData, symbol]);

    const handleFieldUpdate = (name: keyof OptionsTradeTicketViewModel, value: OptionsTradeTicketViewModel[keyof OptionsTradeTicketViewModel]) => {
        // Futures only: If it's a price, and the value is divisible by the tick size, it's valid
        if (isFuture && priceInputFields.includes(name)) {
            const isInputValid = !isNaN(value as number) && !floatMath(value as number, tickSize, (v, s) => v % s);
            const newTickSizeError = isInputValid ? [...tickSizeError.filter((x) => x !== name)] : [...tickSizeError, name];
            setTickSizeError(newTickSizeError);
        }

        setViewModel({ [name]: value });
    };

    const formatter = (number: number) => FormatNumber.toCommas(number, true, 0);

    const handleAccountSelect = useCallback(() => setViewModel({ awaitingConfirmation: false }), [setViewModel]);

    return (
        <Flex column>
            {awaitingConfirmation ? (
                <TradeCancelHeader
                    logo={logo}
                    text={text.tradeTicket.input}
                    showLogo={modifyingOrder}
                    symbol={symbol}
                    title={headerTitle}
                    onCancel={() => setViewModel({ awaitingConfirmation: false, validateResponse: null })}
                />
            ) : (
                <TradeOptionHeader />
            )}
            <TradeTicketSection noBorder style={{ paddingTop: 20, paddingBottom: 10 }}>
                <TradeTicketAccountDropdown
                    assetFamily={assetFamily}
                    balanceType={tradeAction}
                    defaultToFirst={'if-only-one'}
                    disabled={modifyingOrder}
                    onSelect={handleAccountSelect}
                    skipInitialSelect={modifyingOrder || !!selectedAccountNumber}
                    style={{ marginBottom: 15 }}
                    symbol={symbol}
                    useOptionsBuyingPower={useOptionsBuyingPower}
                    isByAssetClass
                />
                {['stop', 'stoplimit'].includes(orderType) && (
                    <>
                        <PriceInputSplit
                            arrows={isFuture}
                            formatOptions={moneyFormatOptions}
                            formatter={(v: number) => formatPrice(v, meta)}
                            initialValue={initialStopPrice}
                            key={stopInputKey}
                            label={text.tradeTicket.input.stopPrice}
                            onValueChange={(v) => handleFieldUpdate('stopPrice', v)}
                            step={tickSize}
                            showDollarSign={showDollarSign}
                            {...getErrorProps('stopPrice')}
                        />
                        <BidAskButtons
                            bid={quote?.bid || 0}
                            ask={quote?.ask || 0}
                            askLabel={`${text.tradeTicket.input.ask}: ${ac.formatPrice(quote?.ask || 0, meta)}`}
                            bidLabel={`${text.tradeTicket.input.bid}: ${ac.formatPrice(quote?.bid || 0, meta)}`}
                            onValueChange={(v) => {
                                setViewModel({
                                    initialStopPrice: v,
                                    stopPrice: undefined
                                });
                                setStopInputKey(limitInputKey + 1);
                            }}
                        />
                    </>
                )}
                {['limit', 'stoplimit'].includes(orderType) && (
                    <>
                        <PriceInputSplit
                            arrows={isFuture}
                            formatOptions={moneyFormatOptions}
                            formatter={(v: number) => formatPrice(v, meta)}
                            initialValue={initialLimitPrice}
                            key={limitInputKey}
                            label={orderType === 'limit' ? text.tradeTicket.input.limitPrice : text.tradeTicket.input.stopLimitPrice}
                            testId='trade-ticket-limit-price'
                            onValueChange={(v) => handleFieldUpdate('limitPrice', v)}
                            step={tickSize}
                            showDollarSign={showDollarSign}
                            {...getErrorProps('limitPrice')}
                        />
                        <BidAskButtons
                            bid={quote?.bid || 0}
                            ask={quote?.ask || 0}
                            askLabel={`${text.tradeTicket.input.ask}: ${ac.formatPrice(quote?.ask || 0, meta)}`}
                            bidLabel={`${text.tradeTicket.input.bid}: ${ac.formatPrice(quote?.bid || 0, meta)}`}
                            onValueChange={(v) => {
                                setViewModel({
                                    initialLimitPrice: v,
                                    limitPrice: undefined
                                });
                                setLimitInputKey(limitInputKey + 1);
                            }}
                        />
                    </>
                )}
                {!isFuture && (
                    <OptionContractSummaryRow
                        action={action}
                        ask={formatPrice(quote?.ask, meta)}
                        bid={formatPrice(quote?.bid, meta)}
                        expDate={SafeFormat(optSym.expDate, 'MM/dd/yyyy')}
                        last={formatPrice(quote?.last ? quote?.last : tradeAction === TradeActions.Buy ? quote?.ask : quote?.bid, meta)}
                        putCall={putCall}
                        onCancel={() => setViewModel({ symbol: null })}
                        showCloseButton={false}
                        strike={toMoneyOpt2(meta?.option?.strikePrice, { decimalPlaces, hideCurrencySymbol: isFuture })}
                        symbol={fsym.noPrefix}
                    />
                )}
                <OptionUnderlyingHoldingsDisplay accountNumber={selectedAccountNumber} underlyingSymbol={meta?.option?.underlyingSymbol} />
                <QuantityInputSplit
                    accountNumber={selectedAccountNumber}
                    arrows={true}
                    formatter={formatter}
                    initialValue={initialQuantity}
                    key={quantityInputKey}
                    label={T((t) => t.general.contracts(0))}
                    liquidateOnSellAll={false}
                    onBuySellAll={() => setQuantityInputKey(quantityInputKey + 1)}
                    onValueChange={(v) => handleFieldUpdate('quantity', v)}
                    showPosition={true}
                    showSellAll={true}
                    step={1} // Used for shares only, 1 share per tick
                    sublabel={sharesLabel}
                    symbol={symbol}
                    tradeAction={tradeAction}
                />
                {orderType !== 'market' ? <TimeInForceSelect onValueChange={(v) => handleFieldUpdate('timeInForce', v)} /> : null}
            </TradeTicketSection>
            <ImmediateExecutionAdvisory
                action={tradeAction}
                limitPrice={limitPrice}
                orderType={displayOrderType}
                stopPrice={stopPrice}
                quote={quote}
                text={text.tradeTicket.input.advisories}
            />
            <TradeTicketSection style={{ paddingBottom: 20 }}>
                {showAlgo && <AlgoControl />}
                <OptionTotalCostDisplay />
                {meta?.option?.isAdjusted && <Typography variant='body1'>{text.tradeTicket.adjustedOptionsDisclaimer}</Typography>}
                <TradeInputValidationButton disabled={tickSizeError.length > 0 || undefined} />
            </TradeTicketSection>
            <TradeTicketSection noBorder>
                {/* set action to buy to force buying power to show always on equity #108615 */}
                <BuyingPowerDisplayV2 accountNumber={selectedAccountNumber} symbol={symbol} useOptionsBuyingPower={useOptionsBuyingPower} />
            </TradeTicketSection>
        </Flex>
    );
});
